import { Suspense, useEffect, useRef, useState } from 'react';
import { Link, Outlet, useNavigate, useParams, useLocation } from 'react-router';
import {
  BadgeQuestionMark,
  CalendarPlus,
  Check,
  ChevronDown,
  Gift,
  LayoutGrid,
  LogOut,
  Mail,
  Menu,
  MonitorSmartphone,
  PanelsTopLeft,
  ScrollText,
  Settings,
  ShoppingBag,
  SquareCheckBig,
  UserCircle,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { useArrangement } from '@/context/ArrangementContext';
import { useAuth } from '@/context/AuthContext';
import { Loading, LoadingScreen } from '@/components/Page';
import Logo from '@/components/Logo';
import type { ModuleKey } from '@/types';

const MODULE_CONFIG: Record<ModuleKey, { label: string; icon: React.ElementType; path: string }> = {
  oversikt: { label: 'Oversikt', icon: LayoutGrid, path: '' },
  leverandorer: { label: 'Leverandører', icon: ShoppingBag, path: 'leverandorer' },
  gjøremål: { label: 'Sjekkliste', icon: SquareCheckBig, path: 'sjekkliste' },
  gjesteliste: { label: 'Gjesteliste', icon: Users, path: 'gjesteliste' },
  budsjett: { label: 'Budsjett', icon: Wallet, path: 'budsjett' },
  kjøreplan: { label: 'Program', icon: ScrollText, path: 'program' },
  bordplassering: { label: 'Bordplassering', icon: PanelsTopLeft, path: 'bordplassering' },
};

const MODULE_ORDER = Object.keys(MODULE_CONFIG) as ModuleKey[];

const FAQ_URL = 'https://dendagen.no/faq';

const navItemClass = 'flex h-[37px] items-center gap-3 rounded-full px-3.5 text-sm transition-colors';
const navItemState = (isActive: boolean) =>
  isActive ? 'bg-white/15 font-bold text-off-white' : 'text-off-white hover:bg-white/10';
const footerItemClass =
  'flex h-8 w-full items-center gap-3 rounded-full px-3.5 text-xs transition-colors cursor-pointer';
const footerItemState = (isActive: boolean) =>
  isActive ? 'bg-white/15 text-off-white' : 'text-white/55 hover:bg-white/10 hover:text-white';
const iconProps = { className: 'size-[19px] shrink-0', strokeWidth: 1.25 };

function PremiumBadge() {
  return (
    <span className="ml-auto rounded-full bg-laks px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
      Premium
    </span>
  );
}

export default function AppLayout() {
  const { slug } = useParams<{ eventType: string; slug: string }>();
  const { arrangement, arrangements, loading, setActiveArrangement } = useArrangement();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);
  const [userOpen, setUserOpen] = useState(false);
  const userRef = useRef<HTMLDivElement>(null);

  const userName: string | undefined = user?.user_metadata?.full_name ?? user?.user_metadata?.name;
  const [avatarUrl, setAvatarUrl] = useState<string | null>(
    user?.user_metadata?.avatar_url ?? user?.user_metadata?.picture ?? null,
  );
  const userInitial = (userName?.[0] ?? user?.email?.[0])?.toUpperCase() ?? '?';

  // Resolve arrangement from URL slug
  useEffect(() => {
    if (loading || !slug) return;
    const found = arrangements.find((a) => a.slug === slug);
    if (found) {
      if (found.id !== arrangement?.id) setActiveArrangement(found.id);
    } else if (arrangements.length > 0) {
      navigate('/', { replace: true });
    }
  }, [slug, arrangements, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!switcherOpen) return;
    function handleClick(e: MouseEvent) {
      if (switcherRef.current && !switcherRef.current.contains(e.target as Node)) {
        setSwitcherOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [switcherOpen]);

  useEffect(() => {
    if (!userOpen) return;
    function handleClick(e: MouseEvent) {
      if (userRef.current && !userRef.current.contains(e.target as Node)) {
        setUserOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [userOpen]);

  if (loading || !arrangement) return <LoadingScreen />;

  const basePath = `/${arrangement.eventType}/${arrangement.slug}`;
  const activeModules: ModuleKey[] = arrangement.modules ?? [];

  const currentPath = location.pathname.startsWith(basePath)
    ? location.pathname.slice(basePath.length).replace(/^\//, '')
    : '';

  return (
    <div className="min-h-screen flex bg-surface">
      {/* Mobile backdrop */}
      {menuOpen && <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMenuOpen(false)} />}

      {/* Sidebar — sticky on desktop, slide-in drawer from right on mobile */}
      <aside
        className={`fixed inset-y-0 right-0 z-50 w-64 flex flex-col bg-navy
          lg:sticky lg:top-0 lg:inset-auto lg:right-auto lg:h-screen lg:w-60 lg:z-auto lg:shrink-0
          transition-transform duration-300 ease-in-out lg:transition-none lg:translate-x-0
          ${menuOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header: logo + arrangement switcher */}
        <div className="flex items-start justify-between gap-2 px-5 pt-6 pb-4 lg:pt-7 lg:pb-8">
          <div className="flex-1 min-w-0">
            <Link to={basePath} onClick={() => setMenuOpen(false)}>
              <Logo variant="light" height={52} />
            </Link>
            <div className="relative mt-3" ref={switcherRef}>
              <button
                onClick={() => arrangements.length > 1 && setSwitcherOpen((o) => !o)}
                className={`flex items-center justify-between w-full text-xs text-white/55 transition-colors
                  ${arrangements.length > 1 ? 'hover:text-off-white cursor-pointer' : 'cursor-default'}`}
              >
                <span className="truncate" title={arrangement.name}>
                  {arrangement.name}
                </span>
                {arrangements.length > 1 && (
                  <ChevronDown
                    className={`w-3 h-3 shrink-0 ml-1 transition-transform duration-150 ${switcherOpen ? 'rotate-180' : ''}`}
                  />
                )}
              </button>
              {switcherOpen && (
                <div className="absolute left-0 right-0 top-full mt-1.5 bg-[#2d3a45] border border-white/10 rounded-lg shadow-xl z-50 overflow-hidden py-1">
                  {arrangements.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => {
                        const currentActiveEventSegmentMatch = location.pathname.match(
                          /^\/[^/]+\/[^/]+\/(?<segment>leverandorer|budsjett|sjekkliste|gjesteliste|invitasjoner|nettside|onskeliste|program|bordplassering|innstillinger)$/,
                        );
                        const currentActiveEventSegment = currentActiveEventSegmentMatch?.groups?.segment || null;

                        setActiveArrangement(a.id);
                        navigate(
                          `/${a.eventType}/${a.slug}${currentActiveEventSegment ? `/${currentActiveEventSegment}` : ''}`,
                        );
                        setSwitcherOpen(false);
                        setMenuOpen(false);
                      }}
                      className={`flex items-center gap-2 w-full text-left px-3 py-1.5 text-xs transition-colors cursor-pointer
                        ${a.id === arrangement.id ? 'text-krem' : 'text-white/50 hover:bg-white/8 hover:text-white/90'}`}
                    >
                      <span className="truncate flex-1" title={a.name}>
                        {a.name}
                      </span>
                      {a.id === arrangement.id && <Check className="w-3 h-3 shrink-0 text-krem/60" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={() => setMenuOpen(false)}
            className="lg:hidden text-white/50 hover:text-white transition-colors cursor-pointer mt-0.5 shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex flex-1 flex-col gap-[5px] overflow-y-auto px-3">
          {MODULE_ORDER.map((key) => {
            const mod = MODULE_CONFIG[key];
            const Icon = mod.icon;
            if (!activeModules.includes(key) && key !== 'kjøreplan') return null;
            const fullPath = `${basePath}${mod.path ? `/${mod.path}` : ''}`;
            const isActive = mod.path === '' ? currentPath === '' : currentPath === mod.path;
            return (
              <Link
                key={key}
                to={fullPath}
                onClick={() => setMenuOpen(false)}
                className={`${navItemClass} ${navItemState(isActive)}`}
              >
                <Icon {...iconProps} />
                {mod.label}
              </Link>
            );
          })}

          <Link
            to={`${basePath}/invitasjoner`}
            onClick={() => setMenuOpen(false)}
            className={`${navItemClass} ${navItemState(currentPath === 'invitasjoner')}`}
          >
            <Mail {...iconProps} />
            Invitasjoner
            <PremiumBadge />
          </Link>
          <Link
            to={`${basePath}/nettside`}
            onClick={() => setMenuOpen(false)}
            className={`${navItemClass} ${navItemState(currentPath === 'nettside')}`}
          >
            <MonitorSmartphone {...iconProps} />
            Nettside
            <PremiumBadge />
          </Link>
          <Link
            to={`${basePath}/onskeliste`}
            onClick={() => setMenuOpen(false)}
            className={`${navItemClass} ${navItemState(currentPath === 'onskeliste')}`}
          >
            <Gift {...iconProps} />
            Ønskeliste
          </Link>
        </nav>

        {/* Footer: user + settings */}
        <div className="flex flex-col border-t border-white/80 px-3 py-3">
          <Link to="/ny" onClick={() => setMenuOpen(false)} className={`${footerItemClass} ${footerItemState(false)}`}>
            <CalendarPlus {...iconProps} />
            Nytt arrangement
          </Link>
          <div className="relative" ref={userRef}>
            <button
              onClick={() => setUserOpen((o) => !o)}
              className={`${footerItemClass} ${footerItemState(location.pathname === '/profil')}`}
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt=""
                  className="size-[19px] rounded-full object-cover shrink-0"
                  onError={() => setAvatarUrl(null)}
                />
              ) : (
                <div className="size-[19px] rounded-full bg-mint flex items-center justify-center text-[10px] font-semibold text-navy shrink-0">
                  {userInitial}
                </div>
              )}
              <span className="truncate">{userName ?? 'Konto'}</span>
            </button>
            {userOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-1.5 bg-[#2d3a45] border border-white/10 rounded-lg shadow-xl z-50 p-3 space-y-2">
                {userName && <p className="text-xs text-white/80 truncate font-medium">{userName}</p>}
                <p className="text-[11px] text-white/40 truncate">{user?.email}</p>
                <div className="border-t border-white/10" />
                <Link
                  to="/profil"
                  onClick={() => {
                    setUserOpen(false);
                    setMenuOpen(false);
                  }}
                  className="flex items-center gap-2 w-full text-xs text-white/60 hover:text-white transition-colors cursor-pointer py-0.5"
                >
                  <UserCircle className="w-3.5 h-3.5" />
                  Profil
                </Link>
                <Link
                  to="/loggut"
                  className="flex items-center gap-2 w-full text-xs text-white/60 hover:text-white transition-colors cursor-pointer py-0.5"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  Logg ut
                </Link>
              </div>
            )}
          </div>
          <Link
            to={`${basePath}/innstillinger`}
            onClick={() => setMenuOpen(false)}
            className={`${footerItemClass} ${footerItemState(currentPath === 'innstillinger')}`}
          >
            <Settings {...iconProps} />
            Innstillinger
          </Link>
          <a
            href={FAQ_URL}
            target="_blank"
            rel="noreferrer"
            className={`${footerItemClass} ${footerItemState(false)}`}
          >
            <BadgeQuestionMark {...iconProps} />
            FAQ
          </a>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-30 bg-navy border-b border-white/10">
          <div className="flex h-[61px] items-center justify-between px-4">
            <Link to={basePath}>
              <Logo variant="light" height={26} />
            </Link>
            <button
              onClick={() => setMenuOpen(true)}
              className="text-white/70 hover:text-white transition-colors cursor-pointer"
            >
              <Menu className="size-6" />
            </button>
          </div>
        </header>

        <Suspense fallback={<Loading />}>
          <Outlet context={{ basePath, arrangement }} />
        </Suspense>
      </div>
    </div>
  );
}

// Hook for pages to get basePath
export { useOutletContext as useAppLayout } from 'react-router';
